// redux/slices/faqSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchNeedAssistanceList,
  createNeedAssistance,
  updateNeedAssistance,
  NeedAssistanceItem,
} from "../../apis/faqApis";

interface FaqState {
  data: NeedAssistanceItem[];
  loading: boolean;
  error: string | null;
  needRefresh: boolean;
}

const initialState: FaqState = {
  data: [],
  loading: false,
  error: null,
  needRefresh: true, // initially true to trigger first fetch
};

// Async Thunks
export const fetchFaqs = createAsyncThunk("faq/fetchFaqs", async () => {
  const data = await fetchNeedAssistanceList();
  return data;
});

export const addFaq = createAsyncThunk(
  "faq/addFaq",
  async (
    form: { question: string; answer: string; active: boolean },
    { rejectWithValue }
  ) => {
    try {
      const created = await createNeedAssistance({
        assistanceQuestion: form.question,
        assistanceAnswer: form.answer,
        isActive: form.active,
      });
      return created;
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  }
);

export const editFaq = createAsyncThunk(
  "faq/editFaq",
  async (
    {
      id,
      form,
    }: {
      id: string;
      form: { question: string; answer: string; active: boolean };
    },
    { rejectWithValue }
  ) => {
    try {
      await updateNeedAssistance(id, {
        assistanceQuestion: form.question,
        assistanceAnswer: form.answer,
        isActive: form.active,
      });

      return {
        id,
        ...form,
        updatedAt: new Date().toISOString(),
      };
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  }
);

const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {
    setFaqRefresh: (state, action: PayloadAction<boolean>) => {
      state.needRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.needRefresh = false; // mark refresh complete
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch FAQs";
      })

      .addCase(addFaq.fulfilled, (state) => {
        state.needRefresh = true;
      })

      .addCase(editFaq.fulfilled, (state) => {
        state.needRefresh = true;
      });
  },
});

export const { setFaqRefresh } = faqSlice.actions;
export default faqSlice.reducer;
